import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import logoDark from '@/assets/logo-dark.svg';
import { useTranslation } from 'react-i18next';
import { useDirection } from '@/hooks/useDirection';

/* ── privacy notice version ─────────────────────────────────────────── */
export const PRIVACY_NOTICE_VERSION = '2.0';
export const PRIVACY_NOTICE_DATE = '2026-03-30';

/** Renders translated HTML content safely (only our own i18n strings) */
function Html({ tKey, className }: { tKey: string; className?: string }) {
  const { t } = useTranslation();
  return <span className={className} dangerouslySetInnerHTML={{ __html: t(tKey) }} />;
}

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const dir = useDirection();
  const isAr = i18n.language === 'ar';

  const backPath = location.state?.from || '/';

  const formattedDate = new Date(PRIVACY_NOTICE_DATE).toLocaleDateString(
    isAr ? 'ar-SA' : 'en-US',
    { month: 'long', day: 'numeric', year: 'numeric' }
  );

  return (
    <div className="min-h-screen bg-background" dir={dir.dir}>
      <header className="border-b border-border/60 bg-card/80 backdrop-blur-md sticky top-0 z-20">
        <div className="max-w-4xl mx-auto flex items-center gap-3 px-4 sm:px-6 py-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(backPath)} className="shrink-0">
            <ArrowLeft className="w-5 h-5 rtl-flip" />
          </Button>
          <div className="flex items-center gap-2">
            <img src={logoDark} alt="Fork" className="w-7 h-auto" />
            <span className="font-bold text-foreground font-display">
              {t('privacy.brandName')}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Card className="p-6 sm:p-10 rounded-2xl border-border shadow-card space-y-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-display text-foreground">{t('privacy.title')}</h1>
            <p className="text-sm text-muted-foreground mt-2">
              {t('privacy.version', { version: PRIVACY_NOTICE_VERSION, date: formattedDate })}
            </p>
          </div>

          {/* 1 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s1Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <Html tKey="privacy.s1Text" />
            </p>
          </section>

          {/* 2 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s2Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s2Intro')}</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ps-2">
              <li><Html tKey="privacy.s2Identity" /></li>
              <li><Html tKey="privacy.s2Professional" /></li>
              <li><Html tKey="privacy.s2Application" /></li>
              <li><Html tKey="privacy.s2Usage" /></li>
              <li><Html tKey="privacy.s2Organization" /></li>
            </ul>
          </section>

          {/* 3 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s3Title')}</h2>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ps-2">
              <li>{t('privacy.s3Item1')}</li>
              <li>{t('privacy.s3Item2')}</li>
              <li>{t('privacy.s3Item3')}</li>
              <li>{t('privacy.s3Item4')}</li>
              <li>{t('privacy.s3Item5')}</li>
              <li>{t('privacy.s3Item6')}</li>
            </ul>
          </section>

          {/* 4 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s4Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s4Intro')}</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ps-2">
              <li><Html tKey="privacy.s4Contract" /></li>
              <li><Html tKey="privacy.s4Consent" /></li>
              <li><Html tKey="privacy.s4Legal" /></li>
              <li><Html tKey="privacy.s4Legitimate" /></li>
            </ul>
          </section>

          {/* 5 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s5Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s5Intro')}</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ps-2">
              <li><Html tKey="privacy.s5Ocr" /></li>
              <li><Html tKey="privacy.s5Scoring" /></li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s5Fairness')}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <Html tKey="privacy.s5Fallback" />
            </p>
          </section>

          {/* 6 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s6Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s6Intro')}</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ps-2">
              <li><Html tKey="privacy.s6Employers" /></li>
              <li><Html tKey="privacy.s6Ai" /></li>
              <li><Html tKey="privacy.s6Infra" /></li>
              <li><Html tKey="privacy.s6Legal" /></li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s6Transfer')}</p>
          </section>

          {/* 7 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s7Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s7Intro')}</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ps-2">
              <li><Html tKey="privacy.s7Cv" /></li>
              <li><Html tKey="privacy.s7Interview" /></li>
              <li><Html tKey="privacy.s7Account" /></li>
              <li><Html tKey="privacy.s7Audit" /></li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s7Note')}</p>
          </section>

          {/* 8 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s8Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s8Intro')}</p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5 ps-2">
              <li><Html tKey="privacy.s8Access" /></li>
              <li><Html tKey="privacy.s8Correction" /></li>
              <li><Html tKey="privacy.s8Deletion" /></li>
              <li><Html tKey="privacy.s8Withdraw" /></li>
              <li><Html tKey="privacy.s8Portability" /></li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s8Note')}</p>
          </section>

          {/* 9 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s9Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s9Text')}</p>
          </section>

          {/* 10 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s10Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">{t('privacy.s10Text')}</p>
          </section>

          {/* 11 */}
          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-foreground">{t('privacy.s11Title')}</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              <Html tKey="privacy.s11Text" />
            </p>
          </section>

          <div className="pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">{t('privacy.footer')}</p>
          </div>
        </Card>
      </main>
    </div>
  );
}
